import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import PlayStoreButton from "@/components/PlayStoreButton";
import { useLanguage } from "@/context/LanguageContext";

const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, id: "nav-home" },
    { to: "/#features", label: t.nav.features, id: "nav-features", isHash: true },
    { to: "/#pricing", label: t.nav.pricing, id: "nav-pricing", isHash: true },
    { to: "/about", label: t.nav.about, id: "nav-about" },
    { to: "/contact", label: t.nav.contact, id: "nav-contact" },
  ];

  // Robust anchor navigation: works from any route by navigating home first then scrolling.
  const handleAnchor = (e, hash) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/${hash}`;
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#fdfbf7]/85 backdrop-blur-xl border-b border-blue-900/10 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 flex items-center justify-between h-[72px]">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) =>
            l.isHash ? (
              <a
                key={l.id}
                data-testid={l.id}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to.replace("/", ""))}
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-blue-900/5 transition"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.id}
                to={l.to}
                data-testid={l.id}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  location.pathname === l.to
                    ? "text-blue-900 bg-blue-900/5"
                    : "text-slate-700 hover:text-blue-900 hover:bg-blue-900/5"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <PlayStoreButton size="sm" testId="nav-cta-playstore" />
        </div>

        {/* Mobile: language toggle visible always */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageToggle compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-blue-900/15 bg-white text-blue-900"
            aria-label="Toggle navigation"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#fdfbf7] border-t border-blue-900/10 px-5 py-6"
        >
          <div className="flex flex-col gap-1">
            {links.map((l) =>
              l.isHash ? (
                <a
                  key={l.id}
                  href={l.to}
                  onClick={(e) => handleAnchor(e, l.to.replace("/", ""))}
                  className="px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-blue-900/5"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.id}
                  to={l.to}
                  className="px-4 py-3 rounded-xl text-base font-medium text-slate-800 hover:bg-blue-900/5"
                >
                  {l.label}
                </Link>
              )
            )}
          </div>
          <div className="mt-5 flex items-center justify-end gap-3">
            <PlayStoreButton size="sm" testId="mobile-cta-playstore" />
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
